import {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useState,
  FormEvent,
} from "react";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import BasicModal from "../BasicModal";
import MainWithdrawStep from "./MainWithdrawStep";
import ConfirmWithdrawStep from "./ConfirmWithdrawStep";
import SuccessWithdrawStep from "./SuccessWithdrawStep";
import { CurrenciesStore, GlobalStore, UserStore } from "stores";
import { claimFntToken } from "api/fntClaim";
import { authStepUp, waitForOneTimeAccessToken } from "api/auth";
import type { ModalStepsType, CurrencyInterface } from "helpers/types";
import type { FntClaimInfo, ClaimFntTokenReq } from "api/types/fntClaim";

interface WithdrawModalProps {
  isOpen: boolean;
  onClose: () => void;
  claimInfo: FntClaimInfo[] | null;
  getFntClaimData: () => void;
}
const FntClaimWithdrawModal: FC<WithdrawModalProps> = ({
  isOpen,
  onClose,
  claimInfo,
  getFntClaimData,
}) => {
  const { t } = useTranslation();
  const { currencies } = CurrenciesStore;
  const { authOneTimeAccessToken } = UserStore;
  const [step, setStep] = useState<ModalStepsType>("main");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [skipConfirm, setSkipConfirm] = useState<boolean>(false);
  const [chosenNetwork, setChosenNetwork] = useState<string>("");
  const [currentClaimInfo, setCurrentClaimInfo] = useState<FntClaimInfo | null>(
    null
  );
  const [claimInfos, setClaimInfos] = useState<FntClaimInfo[] | null>(null);
  const [withdrawAddress, setWithdrawAddress] = useState<string>("");
  const [withdrawMemo, setWithdrawMemo] = useState<string>("");
  const [paymentType, setPaymentType] = useState<CurrencyInterface | undefined>(
    undefined
  );

  useEffect(() => {
    if (isOpen && claimInfo) {
      const firstNotEmptyClaim = claimInfo.find(
        ({ amount }) => Number(amount) > 0
      );
      setCurrentClaimInfo(firstNotEmptyClaim || claimInfo[0]);

      setClaimInfos(claimInfo.filter(({ amount }) => Number(amount) > 0));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const hasMemo = useMemo<boolean>(() => {
    if (chosenNetwork && paymentType?.useMemo) {
      return true;
    }
    return false;
  }, [chosenNetwork, paymentType]);

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (step === "main" && !skipConfirm) {
      setStep("confirm");
      return;
    }
    try {
      await authStepUp("STEP_UP_CLAIM");

      await waitForOneTimeAccessToken();

      const withdrawData: ClaimFntTokenReq = {
        toAddress: withdrawAddress.replaceAll(" ", ""),
        oneTimePassword: authOneTimeAccessToken!,
        cabinet: currentClaimInfo?.cabinet || "",
      };

      if (hasMemo && withdrawMemo) {
        withdrawData.toMemo = withdrawMemo;
      }

      setIsLoading(true);

      await claimFntToken(withdrawData);
      getFntClaimData();
      setStep("success");
    } catch (err: any) {
      if (err?.response?.data?.message) {
        GlobalStore.setError(err.response.data.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && currentClaimInfo) {
      setPaymentType(
        currencies?.find(({ name }) => name === currentClaimInfo.currencyName)!
      );
      setChosenNetwork(currentClaimInfo.networkCode);
    }
  }, [currentClaimInfo, currencies, isOpen]);

  const closeModal = () => {
    if (isLoading) {
      return;
    }
    onClose();
  };

  const resetState = useCallback(() => {
    setStep("main");
    setChosenNetwork("");
    setWithdrawAddress("");
    setWithdrawMemo("");
    setPaymentType(undefined);
    setCurrentClaimInfo(null);
  }, []);

  return (
    <BasicModal
      isOpen={isOpen}
      onExited={resetState}
      onClose={closeModal}
      title={`Token ${t("CLAIM")}`}
      showLoader={isLoading}
    >
      {step === "main" && (
        <MainWithdrawStep
          onSubmit={onSubmit}
          skipConfirm={skipConfirm}
          setSkipConfirm={setSkipConfirm}
          onClose={closeModal}
          paymentType={paymentType}
          claimInfos={claimInfos}
          setCurrentClaimInfo={setCurrentClaimInfo}
          claimInfo={currentClaimInfo}
          currencies={currencies}
          chosenNetwork={chosenNetwork}
          setWithdrawAddress={setWithdrawAddress}
          withdrawAddress={withdrawAddress}
          setIsLoading={setIsLoading}
          withdrawMemo={withdrawMemo}
          hasMemo={hasMemo}
          setWithdrawMemo={setWithdrawMemo}
          isLoading={isLoading}
        />
      )}
      {step === "confirm" && (
        <ConfirmWithdrawStep
          onSubmit={onSubmit}
          skipConfirm={skipConfirm}
          setSkipConfirm={setSkipConfirm}
          onBack={() => setStep("main")}
          onClose={closeModal}
          claimInfo={currentClaimInfo}
          currencies={currencies}
          isLoading={isLoading}
        />
      )}
      {step === "success" && (
        <SuccessWithdrawStep
          onClose={closeModal}
          claimInfo={currentClaimInfo}
          currencies={currencies}
        />
      )}
    </BasicModal>
  );
};

export default observer(FntClaimWithdrawModal);
